import { GameType } from "@redotech/order-tracking-common/tracking-page-definition";
import type { KAPLAYCtx } from "kaplay";
import { TrackingPageGame, VictoryState } from "../game";
import { GameCompleteCallback } from "../game-controls";
import { createCameraSystem } from "./camera-system";
import * as C from "./constants";
import { createLevel } from "./level";
import { voxelRevolutionLevel } from "./levels/voxel-revolution";
import { createMasonryBackground } from "./masonry-background";
import { createPlayer } from "./player";
import { createVictorySequence } from "./victory-sequence";

const DELIVERY_DASH_ASSETS_URL =
  "https://assets.getredo.com/marketing/games/delivery-dash";
const USE_LOCALHOST_AUDIO = false;

export const deliveryDashGame: TrackingPageGame = {
  gameType: GameType.DELIVERY_DASH,
  initializeGame: initializeDeliveryDash,
  strings: {
    victory: "You completed the level!",
    failure: "You crashed! Better luck next time.",
    credits:
      'Voxel Revolution" Kevin MacLeod (incompetech.com)\nLicensed under Creative Commons: By Attribution 4.0 License\nhttp://creativecommons.org/licenses/by/4.0/',
    instructions:
      "Tap the screen or press space, w, or up to jump over obstacles.\nAvoid spikes and running into platforms.\nReach the end to win!",
  },
};

export function initializeDeliveryDash(
  k: KAPLAYCtx,
  onGameComplete: GameCompleteCallback,
) {
  k.setBackground(40, 40, 50);
  const bgMasonry = createMasonryBackground(k);

  const bgMusicUrl = USE_LOCALHOST_AUDIO
    ? "http://localhost:8000/voxel-revolution.mp3"
    : `${DELIVERY_DASH_ASSETS_URL}/voxel-revolution.mp3`;
  const deathSoundUrl = USE_LOCALHOST_AUDIO
    ? "http://localhost:8000/splat.mp3"
    : `${DELIVERY_DASH_ASSETS_URL}/splat.mp3`;
  k.loadSound("bg-music", bgMusicUrl);
  k.loadSound("death", deathSoundUrl);
  k.loadSound("charge", `${DELIVERY_DASH_ASSETS_URL}/charge.mp3`);
  k.loadSound("whoosh", `${DELIVERY_DASH_ASSETS_URL}/whoosh.mp3`);
  k.loadSound("success", `${DELIVERY_DASH_ASSETS_URL}/success.mp3`);

  const { player, jump, jumpRelease } = createPlayer(k);
  const level = createLevel(k, voxelRevolutionLevel);
  const victorySequence = createVictorySequence(k, player);

  const cameraSystem = createCameraSystem(k, player);

  const backgroundMusic = k.play("bg-music", {
    loop: true,
    volume: C.BACKGROUND_MUSIC_VOLUME,
  });

  let gameRunning = true;
  let score = 0;
  let playerWorldX = 0;

  k.onUpdate(() => {
    if (!gameRunning && !victorySequence.isActive()) {
      return;
    }

    const dt = k.dt();

    // Only update world movement if not in debug mode
    if (!C.DEBUG_LAMP_AT_START) {
      playerWorldX += C.PLAYER_SPEED * dt;
      player.worldX = playerWorldX;

      level.updateWorld(playerWorldX);

      // Update background parallax (slower than world movement)
      bgMasonry.pos.x =
        -playerWorldX * C.GRID_CELL_SIZE * C.BACKGROUND_PARALLAX_SPEED;

      cameraSystem.update(victorySequence.isActive());
    } else {
      // In debug mode, just render initial objects
      level.updateWorld(0);
    }

    if (victorySequence.isActive()) {
      const completed = victorySequence.update(dt);
      if (completed) {
        endGame(true);
      }
      return; // Don't run normal game logic during victory sequence
    }

    const levelWidth = voxelRevolutionLevel.grid.width;
    // Update score based on progress to end
    const progress = Math.min(playerWorldX / levelWidth, 1.0);
    score = Math.floor(progress * C.MAX_SCORE);

    if (level.isLevelComplete(playerWorldX)) {
      gameRunning = false;
      victorySequence.start();
    }
  });

  const jumpIfAllowed = () => {
    if (gameRunning) {
      jump();
    }
  };

  const jumpReleaseIfAllowed = () => {
    if (gameRunning) {
      jumpRelease();
    }
  };

  k.onKeyPress("space", jumpIfAllowed);
  k.onKeyPress("up", jumpIfAllowed);
  k.onKeyPress("w", jumpIfAllowed);
  k.onMousePress(jumpIfAllowed);
  k.onTouchStart(jumpIfAllowed);

  k.onKeyRelease("space", jumpReleaseIfAllowed);
  k.onKeyRelease("up", jumpReleaseIfAllowed);
  k.onKeyRelease("w", jumpReleaseIfAllowed);
  k.onMouseRelease(jumpReleaseIfAllowed);
  k.onTouchEnd(jumpReleaseIfAllowed);

  const endGame = (victory: boolean) => {
    if (victory) {
      onGameComplete({ finalScore: score, victoryState: VictoryState.VICTORY });
    } else {
      k.play("death", { volume: C.DEATH_SOUND_VOLUME });
      onGameComplete({ finalScore: score, victoryState: VictoryState.FAILURE });
    }

    backgroundMusic.stop();
    k.destroyAll("*");
    gameRunning = false;
  };

  player.onCollide("spike", () => {
    if (gameRunning) {
      endGame(false);
    }
  });

  player.onCollide("platform", (platform, collision) => {
    if (!gameRunning) {
      return;
    }
    if (!collision) {
      return;
    }

    const hitKillZone =
      collision.isLeft() || collision.isRight() || collision.isTop();

    if (hitKillZone) {
      endGame(false);
    }
  });

  return {
    moveUp: jump,
    moveDown: () => {},
    moveLeft: () => {},
    moveRight: () => {},
    stopMovement: () => {},
  };
}
